import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, UserType } from "../../../contexts/AuthContext";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { ROUTES } from "../../../routes";

type Step = "type" | "account";

const USER_TYPE_OPTIONS: { value: UserType; label: string; description: string; icon: string }[] = [
  {
    value: "restaurant",
    label: "Un restaurant",
    description: "Gérez la carte des vins de votre établissement",
    icon: "🍽️",
  },
  {
    value: "caviste",
    label: "Un caviste",
    description: "Présentez et valorisez votre sélection de vins",
    icon: "🍾",
  },
];

export function SignupPage() {
  const { signUp, setUserType } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("type");
  const [selectedType, setSelectedType] = useState<UserType>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    setLoading(true);
    try {
      await signUp(email, password);
      setUserType(selectedType);
      navigate(ROUTES.VINAI, { replace: true });
    } catch (err: any) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen lg:flex">
      <section className="relative hidden flex-1 overflow-hidden bg-accent px-12 py-14 text-[#FFF6EE] lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-16 -top-24 h-[360px] w-[360px] rounded-full bg-white/15 blur-[2px]" />
        <div className="absolute -bottom-20 -left-14 h-[280px] w-[280px] rounded-full bg-[#7A2438]/35" />
        <div className="absolute left-[58%] top-[44%] h-[140px] w-[140px] rounded-full bg-[#CC9A3E]/35" />

        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-[16px] bg-[#FFF6EE] font-display text-[24px] font-extrabold text-accent">v</div>
          <span className="font-display text-[28px] font-bold tracking-[-0.05em]">vinIA</span>
        </div>

        <div className="relative max-w-[480px]">
          <h1 className="font-display text-[56px] font-bold leading-[0.98] tracking-[-0.06em]">
            Créez votre studio de cave en quelques minutes.
          </h1>
          <p className="mt-6 text-[18px] leading-8 text-[#FFF6EE]/90">
            Générez vos contenus, structurez votre cave et publiez une carte publique à votre image.
          </p>
        </div>

        <div className="relative flex items-center gap-4 text-[14px] text-[#FFF6EE]/80">
          <span>Carte publique</span>
          <span>·</span>
          <span>Commandes</span>
          <span>·</span>
          <span>IA multilingue</span>
        </div>
      </section>

      <section className="flex flex-1 items-center justify-center px-5 py-12 lg:px-10">
        <div className="w-full max-w-[410px]">
          <div className="mb-8 flex items-center justify-center gap-2">
            <StepDot active={step === "type"} done={step === "account"} label="1" />
            <div className="h-px w-10 bg-border" />
            <StepDot active={step === "account"} done={false} label="2" />
          </div>

          {step === "type" && (
            <>
              <div className="mb-8">
                <h1 className="font-display text-[36px] font-bold tracking-[-0.05em] text-foreground">Bienvenue sur VinIA</h1>
                <p className="mt-2 text-[15px] text-secondary">Vous êtes…</p>
              </div>

              <div className="space-y-3">
                {USER_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSelectedType(opt.value)}
                    className={`w-full rounded-[20px] border p-5 text-left transition-all ${selectedType === opt.value ? "border-accent-border bg-accent/10 shadow-[0_16px_36px_rgba(217,102,60,0.08)]" : "border-border bg-surface hover:border-accent/30 hover:-translate-y-0.5"}`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-2xl">{opt.icon}</span>
                      <div>
                        <p className="text-[16px] font-semibold text-foreground">{opt.label}</p>
                        <p className="mt-1 text-[13px] text-secondary">{opt.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <Button
                variant="primary"
                disabled={!selectedType}
                onClick={() => setStep("account")}
                className="mt-6 w-full"
              >
                Continuer
              </Button>

              <p className="mt-6 text-center text-[14px] text-secondary">
                Déjà un compte ?{" "}
                <Link to={ROUTES.LOGIN} className="font-semibold text-accent transition-opacity hover:opacity-75">
                  Se connecter
                </Link>
              </p>
            </>
          )}

          {step === "account" && (
            <>
              <div className="mb-8">
                <h1 className="font-display text-[36px] font-bold tracking-[-0.05em] text-foreground">Créer un compte</h1>
                <p className="mt-2 text-[15px] text-secondary">
                  Compte {selectedType === "restaurant" ? "restaurant" : "caviste"}.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Input
                  label="Mot de passe"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Input
                  label="Confirmer le mot de passe"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />

                {error && (
                  <p className="rounded-[16px] border border-error/20 bg-error-bg px-4 py-3 text-[13px] text-error">
                    {error}
                  </p>
                )}

                <Button type="submit" variant="primary" disabled={loading} className="w-full">
                  {loading ? "Création…" : "Créer mon compte"}
                </Button>
              </form>

              <button
                type="button"
                onClick={() => { setStep("type"); setError(""); }}
                className="mt-4 w-full text-center text-[14px] font-medium text-secondary transition-colors hover:text-foreground"
              >
                ← Retour
              </button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function StepDot({ active, done, label }: { active: boolean; done: boolean; label: string }) {
  return (
    <div
      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
        done
          ? "bg-accent text-white"
          : active
          ? "bg-accent text-white"
          : "bg-surface border border-border text-muted"
      }`}
    >
      {done ? "✓" : label}
    </div>
  );
}

function getErrorMessage(code: string): string {
  switch (code) {
    case "auth/email-already-in-use":
      return "Cette adresse e-mail est déjà utilisée.";
    case "auth/invalid-email":
      return "Adresse e-mail invalide.";
    case "auth/weak-password":
      return "Le mot de passe est trop faible.";
    default:
      return "Une erreur est survenue. Réessayez.";
  }
}
